import React, { useEffect, useMemo } from 'react';
import { useMediaQuery } from 'react-responsive';

import { ErrorBoundary } from 'components/Common/Errors/ErrorBoundary/ErrorBoundary';
import CarePictogramsItem from './Item/CarePictogramsItem';
import DefaultItem, { Props } from './Item/DefaultItem';
import DescriptionItem from './Item/DescriptionItem';
import EcoTexItem from './Item/EcoTexItem';
import { SelectedProductAttributes } from 'types/ProductPageV2/Products';
import { MOBILE_AND_TABLET_PORTRAIT } from 'settings/mediaQuery';

import styles from './ProductDescription.module.scss';

/**
 * Product page of website. Contains details product data.
 */
function ProductDescription({
  productAttributes,
  handleContentHeight,
}: {
  /** List of product attributes */
  productAttributes: SelectedProductAttributes;
  handleContentHeight?: () => void;
}): JSX.Element {
  const isMobileAndPortraitTablet = useMediaQuery({
    query: MOBILE_AND_TABLET_PORTRAIT,
  });

  const contentItems: ContentItems = {
    description: {
      attrCode: 'description',
      component: DescriptionItem,
    },
    columns: {
      left: [
        'nomModele',
        'metaLot',
        'metaMarque',
        'metaCoupe',
        'metaType',
        'metaArmature',
        'metaFermeture',
        'metaForme',
        'dimension',
        'metaContenance',
        'metaPoids',
        'metaGrammage',
        'densite',
        'metaFinition',
        'metaMotif',
        'advantages',
        'heelHeight',
        'metaLargeur',
      ],
      right: [
        'composition',
        'entretien',
        'metaIngredients',
        'metaConseil',
        'metaPrecaution',
        'metaMention',
        {
          attrCode: 'entretienV2',
          component: CarePictogramsItem,
        },
        {
          attrCode: 'labelEcoTex',
          component: EcoTexItem,
        },
      ],
    },
  };

  useEffect(() => {
    if (typeof handleContentHeight === 'function') handleContentHeight();
  }, []);

  const getContent = (item: Item) => {
    if (typeof item === 'string') {
      item = { attrCode: item };
    }

    const content = productAttributes[item.attrCode]?.Value;
    if (!content || content === '-') {
      return null;
    }

    const Component = item.component || DefaultItem;

    return (
      <ErrorBoundary key={item.attrCode} fallback={<></>}>
        <Component
          attrCode={item.attrCode}
          content={content}
          label={item.label}
          enableCollapseMode={isMobileAndPortraitTablet}
        />
      </ErrorBoundary>
    );
  };

  const renderContentItems = useMemo(() => {
    return Object.entries(contentItems).map(([key, item]) => {
      if (
        typeof item === 'object' &&
        (item.hasOwnProperty('left') || item.hasOwnProperty('right'))
      ) {
        const groupContent = Object.entries(item).map(([column, items]) => {
          const columnContent = (items as Item[])
            .map((item: Item) => getContent(item))
            .filter(Boolean);

          return columnContent.length ? (
            <div key={column} className={styles[column]}>
              {columnContent}
            </div>
          ) : null;
        });

        return groupContent.some(Boolean) ? (
          <div key={key} className={styles.attrLinesGroup}>
            {groupContent}
          </div>
        ) : null;
      }

      return getContent(item as Item);
    });
  }, [productAttributes, isMobileAndPortraitTablet]);

  return (
    <div id="description" className={styles.productDescription}>
      {renderContentItems}
    </div>
  );
}

export default ProductDescription;

type ItemObj = {
  attrCode: string;
  label?: string | false;
  component?: React.FC<Props>;
};

type Item = string | ItemObj;

type ContentItems = Record<
  string,
  Item | Record<'left' | 'right', Array<Item>>
>;
